import { Box, Typography } from '@mui/material';
import { FONT, RADIUS, glass as glassSurface, hoverLift } from '../../theme/tokens';

// ---------------------------------------------------------------------------
// Panoul — planșa pe care stă orice bloc de conținut.
//
// Vine în trei materiale, alese după ce e dedesubt:
//
//   card   suprafață plină. Pentru liste lungi: un `backdrop-filter` întins pe
//          toată înălțimea paginii costă cadre la derulare pe telefon.
//   glass  material translucid, cu `saturate(180%) blur(24px)`. Pentru blocuri
//          mici, așezate peste un fundal care merită să se vadă.
//   plain  fără fundal și fără muchie, doar geometria.
//
// `onDark` mută muchiile și tenta pe setul pentru bleumarin; fără el, o muchie
// neagră de 9% pe fundal închis pur și simplu dispare.
// ---------------------------------------------------------------------------

const headerCell = {
  fontFamily: FONT.mono,
  fontSize: 11.5,
  letterSpacing: '.1em',
  textTransform: 'uppercase',
};

export default function Panel({
  children,
  variant = 'card',
  onDark = false,
  header,
  interactive = false,
  radius = RADIUS.xl,
  component = 'div',
  sx,
  bodySx,
  ...rest
}) {
  const surface =
    variant === 'glass'
      ? onDark
        ? {
            backgroundColor: 'var(--qf-deep-glass)',
            backdropFilter: 'saturate(180%) blur(24px)',
            WebkitBackdropFilter: 'saturate(180%) blur(24px)',
            border: '1px solid var(--qf-deep-line)',
            '@supports not ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px)))': {
              backgroundColor: 'rgba(255,255,255,.14)',
            },
          }
        : glassSurface()
      : variant === 'plain'
        ? {}
        : {
            backgroundColor: onDark ? 'var(--qf-deep-glass)' : 'var(--qf-card)',
            border: `1px solid ${onDark ? 'var(--qf-deep-line)' : 'var(--qf-line)'}`,
            boxShadow: onDark ? 'none' : 'var(--qf-shadow-sm)',
          };

  return (
    <Box
      component={component}
      sx={{
        position: 'relative',
        minWidth: 0,
        borderRadius: `${radius}px`,
        // Fără asta, colțurile rotunjite nu taie capul de tabel și rândurile
        // dinăuntru, iar muchia panoului iese ruptă exact în colț.
        overflow: 'hidden',
        ...surface,
        ...(interactive ? hoverLift : {}),
        ...sx,
      }}
      {...rest}
    >
      {header && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            px: { xs: 2.25, sm: 2.75 },
            py: 1.5,
            borderBottom: `1px solid ${onDark ? 'var(--qf-deep-line)' : 'var(--qf-line)'}`,
            backgroundColor: onDark ? 'rgba(255,255,255,.05)' : 'var(--qf-glass-tint)',
          }}
        >
          <Typography component="span" sx={{ ...headerCell, color: onDark ? 'var(--qf-deep-ink-2)' : 'var(--qf-ink-2)' }}>
            {header.title}
          </Typography>
          {header.meta && (
            <Typography
              component="span"
              sx={{
                ...headerCell,
                color: onDark ? 'rgba(255,255,255,.5)' : 'var(--qf-ink-3)',
                textAlign: 'right',
              }}
            >
              {header.meta}
            </Typography>
          )}
        </Box>
      )}

      {bodySx ? <Box sx={bodySx}>{children}</Box> : children}
    </Box>
  );
}
